import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadActiveHarborOrder } from '../api/harborOrdersApi';

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function formatReward(order) {
  const xp = Number(order?.rewards?.xp || 0);
  const bronze = order?.rewards?.bronze_label || '';
  const parts = [];
  if (xp > 0) parts.push(`+${xp} XP`);
  if (bronze) parts.push(bronze);
  return parts.join(' · ') || 'Belohnung offen';
}

export default function HarborActiveOrderTask({ entries = [], fallback = null }) {
  const navigate = useNavigate();
  const [activeOrder, setActiveOrder] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  const refreshKey = useMemo(() => {
    const list = Array.isArray(entries) ? entries : [];
    const newest = list[0]?.id || list[0]?.timestamp || '';
    return `${list.length}:${newest}`;
  }, [entries]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const payload = await loadActiveHarborOrder();
      if (!mountedRef.current) return;
      setActiveOrder(payload?.active_order || null);
      setLoaded(true);
    } catch (loadError) {
      if (!mountedRef.current) return;
      setActiveOrder(null);
      setLoaded(true);
      setError(loadError?.message || 'Auftrag konnte nicht geladen werden.');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load, refreshKey]);

  useEffect(() => {
    const handleHarborOrderChange = () => {
      load();
    };

    window.addEventListener('pfandpirat:harbor-order-accepted', handleHarborOrderChange);
    window.addEventListener('pfandpirat:harbor-order-claimed', handleHarborOrderChange);
    window.addEventListener('focus', handleHarborOrderChange);

    return () => {
      window.removeEventListener('pfandpirat:harbor-order-accepted', handleHarborOrderChange);
      window.removeEventListener('pfandpirat:harbor-order-claimed', handleHarborOrderChange);
      window.removeEventListener('focus', handleHarborOrderChange);
    };
  }, [load]);

  if (!loaded && loading) {
    return (
      <div className="pfp-active-harbor-order is-loading" role="status" aria-live="polite">
        <strong>📜 Hafenaufträge werden geprüft …</strong>
      </div>
    );
  }

  if (!activeOrder) {
    return fallback;
  }

  const progress = activeOrder.progress || {};
  const percent = clampPercent(progress.percent);
  const complete = Boolean(progress.is_complete || activeOrder.can_claim);

  return (
    <section className={`pfp-active-harbor-order ${complete ? 'is-complete' : ''}`} aria-label="Aktiver Tagesauftrag">
      <div className="pfp-active-harbor-order__head">
        <span className="pfp-active-harbor-order__icon">{activeOrder.icon || '📜'}</span>
        <div>
          <span className="pfp-active-harbor-order__kicker">Tagesauftrag aktiv</span>
          <h2>{activeOrder.title}</h2>
        </div>
      </div>

      <p>{activeOrder.description}</p>

      <div className="pfp-active-harbor-order__meta">
        <span>Ziel: <strong>{activeOrder.target_label}</strong></span>
        <span>Belohnung: <strong>{formatReward(activeOrder)}</strong></span>
      </div>

      <div className="pfp-active-harbor-order__bar" aria-label={`Fortschritt ${progress.label || ''}`}>
        <span style={{ width: `${percent}%` }} />
      </div>

      <div className="pfp-active-harbor-order__progress">
        <strong>{progress.label || `${percent}% erledigt`}</strong>
        {activeOrder.expires_at ? <span>Läuft bis {new Date(activeOrder.expires_at.replace(' ', 'T')).toLocaleString()}</span> : null}
      </div>

      {error ? <div className="pfp-active-harbor-order__error">{error}</div> : null}

      <div className="pfp-active-harbor-order__actions">
        <button type="button" onClick={() => navigate('/freibeuterhafen?focus=orders')}>Zum Hafen</button>
        {!complete ? (
          <button type="button" className="is-secondary" onClick={load} disabled={loading}>
            {loading ? 'Aktualisiere …' : 'Fortschritt prüfen'}
          </button>
        ) : null}
      </div>
    </section>
  );
}
